import { Tooltip } from "antd";
import React, { useMemo } from "react";
import { convertUtcToLocal } from "../../../library/utils";
import { DATE_TIME_FORMATS } from "../../../utils/dateTimes";
import { AUDIT_LOG_EMPLOYEE_ACTOR_TYPES } from "./auditLog/constants";
import mapAuditLogEntry from "./auditLog/mapAuditLogEntry";

/**
 * A consent change in the conversation feed (`messageType: CONSENT_UPDATE`).
 *
 * These are audit events, not conversation, so they render as a centered
 * one-line system row rather than a bubble — "Auto response changed to Enrolled
 * · By Support EngagedAi · 05:30 AM" (EAI-1807): action, actor and time in one
 * muted 14px line, with the full date still on hover.
 *
 * The message's `content` is the same audit record the Audit log drawer shows
 * (it carries the matching `audit_log_id`), just snake_cased, so the wording and
 * the actor rules come from the shared `mapAuditLogEntry` and the two surfaces
 * cannot drift.
 */
const ConsentUpdateMessage = ({ message, userIdMap, leadName }) => {
  const item = useMemo(() => {
    // `content` is a JSON audit record, not prose. If it is not parseable there
    // is nothing to say, and dumping the raw string into the thread would be
    // worse than staying silent.
    try {
      const payload = JSON.parse(message?.content || "");
      if (!payload || typeof payload !== "object") return null;
      return { ...mapAuditLogEntry(payload, null, leadName), updatedById: payload.updated_by_id };
    } catch {
      return null;
    }
  }, [message?.content, leadName]);

  if (!item) return null;

  // `mapAuditLogEntry` has already applied the attribution rules, so a lead's
  // or the system's row is named there and nothing here may override it — the
  // message can carry a `user` even for an automated change. Only an employee
  // row benefits from the thread's extra knowledge: the payload names the actor
  // by id, and the message record plus the thread's id→name map resolve it when
  // the audit row itself carries no name.
  const needsEmployeeLookup =
    AUDIT_LOG_EMPLOYEE_ACTOR_TYPES.includes(item.updatedByType) && !item.updatedByName;
  const employeeName =
    (+message?.user?.id === +item.updatedById ? message?.user?.fullName : null) ||
    userIdMap?.[+item.updatedById];
  const actorName = (needsEmployeeLookup && employeeName) || item.metaPrefix;

  const sentAt = message?.dateReceived || message?.dateSent;
  const time = convertUtcToLocal(sentAt, DATE_TIME_FORMATS.HH_MM_A);
  const fullTimestamp = convertUtcToLocal(sentAt, DATE_TIME_FORMATS.MMM_D_YYYY_DOT_h_mm_A);

  // "{action} · By {actor} · {time}", dropping any part that did not resolve
  // rather than leaving a dangling separator.
  const parts = [item.title, actorName && `By ${actorName}`, sentAt && time].filter(Boolean);

  return (
    <div className="flex w-full justify-center px-4">
      <Tooltip title={fullTimestamp}>
        <span className="text-center text-sm font-normal leading-[22px] text-grey-500">
          {parts.join(" · ")}
        </span>
      </Tooltip>
    </div>
  );
};

export default ConsentUpdateMessage;
